<template>
  <div class="documents-page-container">
    <v-row>
      <v-col cols="12" md="6" v-for="card in ['totalDocuments', 'totalStorage']" :key="card">
        <v-card>
          <v-card-title>{{ $t(card) }}</v-card-title>
          <v-card-text class="text-h4">
            <v-skeleton-loader v-if="loading" type="text" width="100px" />
            <template v-else>
              {{ card === 'totalDocuments' ? totalDocuments : formatStorage(totalStorage) }}
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-card class="mb-4" style="height: 460px;">
          <v-card-title class="d-flex justify-space-between align-center">
            <span class="text-truncate">{{ $t('documentsByTeamAndUser') }}</span>
            <v-btn
              class="rdrive-chart-export flex-shrink-0"
              icon="mdi-download"
              variant="text"
              size="small"
              density="comfortable"
              :disabled="loading || !teamUserRows.length"
              @click="teamUserChart?.downloadPng()"
            />
          </v-card-title>
          <v-card-text>
            <v-skeleton-loader v-if="loading" type="image" height="400px" />
            <BaseBarChart v-else
              ref="teamUserChart"
              orientation="horizontal"
              :rows="teamUserRows"
              :seriesName="teamUserLevel === 'teams' ? $t('teams') : $t('users')"
              :colorIndex="teamUserColorIndex"
              :onSelect="selectTeamUser"
              :exportFilename="$t('documentsByTeamAndUser') + '.png'"
              :exportTitle="$t('documentsByTeamAndUser')" />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="loading" type="image" height="400px" />
        <v-card v-else class="mb-4" style="height: 460px;">
          <v-card-title class="d-flex justify-space-between align-center">
            {{ $t('documentTypes') }}
            <div class="d-flex align-center">
              <v-btn-toggle
                v-model="fileTypeMetric"
                mandatory
                dense
              >
                <v-btn value="count">{{ $t('count') }}</v-btn>
                <v-btn value="size">{{ $t('size') }}</v-btn>
              </v-btn-toggle>
              <v-btn
                class="rdrive-chart-export ms-1"
                icon="mdi-download"
                variant="text"
                size="small"
                density="comfortable"
                :disabled="!fileTypeRows.length"
                @click="fileTypeChart?.downloadPng()"
              />
            </div>
          </v-card-title>
          <v-card-text>
            <BaseBarChart
              ref="fileTypeChart"
              :rows="fileTypeRows"
              colorBy="bar"
              showLabels
              :labelRotate="45"
              :borderRadius="5"
              :seriesName="$t('documentTypes')"
              :valueAxisName="fileTypeMetric === 'count' ? $t('count') : $t('totalSize')"
              :formatValue="fileTypeMetric === 'count' ? String : formatFileSize"
              :exportFilename="$t('documentTypes') + '.png'"
              :exportTitle="$t('documentTypes')" />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="loading" type="image" height="400px" />
        <v-card v-else class="mb-4" style="height: 460px;">
          <v-card-title class="d-flex justify-space-between align-center">
            {{ $t('documentUploads') }}
            <div class="d-flex align-center">
              <v-btn-toggle
                v-model="uploadsMetric"
                mandatory
                class="ml-4"
              >
                <v-btn value="count">{{ $t('count') }}</v-btn>
                <v-btn value="size">{{ $t('size') }}</v-btn>
              </v-btn-toggle>
              <v-btn
                class="rdrive-chart-export ms-1"
                icon="mdi-download"
                variant="text"
                size="small"
                density="comfortable"
                :disabled="!uploadsRows.length"
                @click="uploadsChart?.downloadPng()"
              />
            </div>
          </v-card-title>
          <v-card-text>
            <BaseLineChart
              ref="uploadsChart"
              :rows="uploadsRows"
              :seriesName="uploadsMetric === 'count' ? $t('totalCount') : $t('totalSize')"
              :labelRotate="45"
              :formatValue="uploadsMetric === 'count' ? String : formatSize"
              :exportFilename="$t('documentUploads') + '.png'"
              :exportTitle="$t('documentUploads')" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="12">
        <v-card>
          <v-card-title class="pa-0">
            <v-container fluid>
              <v-row no-gutters align="center">
                <v-col cols="auto">
                  <v-tabs v-model="activeTab">
                    <v-tab value="all">{{ $t('documentRepository') }}</v-tab>
                    <v-tab value="latest">
                      {{ $t('latestDocuments') }}
                      <v-badge
                        :content="recentDocumentsCount"
                        :model-value="recentDocumentsCount > 0"
                        color="error"
                        inline
                        class="ml-2"
                      ></v-badge>
                    </v-tab>           
                  </v-tabs>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-text-field
                    v-if="activeTab === 'all'"
                    v-model="search"
                    hide-details
                    density="compact"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    class="mt-2 mb-2 search-field"
                  ></v-text-field>
                  <v-text-field
                    v-else
                    v-model="searchLatest"
                    hide-details
                    density="compact"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    class="mt-2 mb-2 search-field"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-title>
          <v-card-text>
            <v-window v-model="activeTab">
              <v-window-item value="latest">               
                <v-skeleton-loader v-if="loading" type="list-item-three-line" :types="{ skeleton: 'list-item' }" :boilerplate="true" :tile="true" :loading="true" />
                <template v-else>
                  <v-data-table
                    :headers="allDocumentsHeaders"
                    :items="filteredLatestDocuments"
                    :items-per-page="20"
                    class="elevation-1"
                    :locale="$i18n.locale"
                    :no-data-text="$t('noData')"
                    :items-per-page-text="$t('itemsPerPage')"
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td @click="handleItemClick(item)" style="cursor: pointer;">
                          <v-icon :icon="item.isFolder ? 'mdi-folder' : 'mdi-file-document'" class="mr-2"></v-icon>
                          {{ item.displayName }}
                        </td>
                        <td>{{ item.fileType }}</td>
                        <td>{{ formatStorage(item.fileSize) }}</td>
                        <td>
                          <v-tooltip :text="formatDate(item.createdDate, true)" location="bottom">
                            <template v-slot:activator="{ props }">
                              <span v-bind="props">{{ formatTimeAgo(item.createdDate) }}</span>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>
                          <v-tooltip :text="item.fullPath" location="top">
                            <template v-slot:activator="{ props }">
                              <div v-bind="props" class="d-flex align-center">
                                <v-icon icon="mdi-folder" size="small" class="mr-2"></v-icon>
                                <span>{{ getParentFolderName(item.parentFolderId) }}</span>
                              </div>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>{{ getTeamName(item.createdByTeamId) }}</td>
                        <td>
                          <v-tooltip
                            :text="getUserName(item.createdByUserId)"
                            location="bottom"
                          >
                            <template v-slot:activator="{ props }">
                              <v-avatar
                                v-bind="props"
                                :size="28"
                                class="mr-1"
                              >
                                <v-img
                                  v-if="getUserAvatar(item.createdByUserId)"
                                  :src="'https://' + getUserAvatar(item.createdByUserId)"
                                  alt="User Avatar"
                                ></v-img>
                                <span
                                  v-else
                                  class="d-flex justify-center align-center text-caption white--text"
                                  :style="{
                                    backgroundColor: getRandomColor(item.createdByUserId),
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%'
                                  }"
                                >
                                  {{ getInitials(item.userDisplayName) }}
                                </span>
                              </v-avatar>
                            </template>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-window-item>
              <v-window-item value="all">            
                <v-skeleton-loader v-if="loading" type="list-item-three-line" :types="{ skeleton: 'list-item' }" :boilerplate="true" :tile="true" :loading="true" />
                <template v-else>
                  <v-breadcrumbs :items="breadcrumbs">
                    <template v-slot:item="{ item }">
                      <v-breadcrumbs-item
                        :title="item.title"
                        :disabled="item.disabled"
                        :href="item.href"
                        @click.prevent="goToPath(item)"
                      >
                        {{ item.title }}
                      </v-breadcrumbs-item>
                    </template>
                  </v-breadcrumbs>

                  <v-data-table
                    :headers="headers"
                    :items="filteredDocuments"
                    :items-per-page="20"
                    class="elevation-1"
                    :locale="$i18n.locale"
                    :no-data-text="$t('noData')"
                    :items-per-page-text="$t('itemsPerPage')"
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td @click="handleItemClick(item)" style="cursor: pointer;">
                          <v-icon :icon="item.isFolder ? 'mdi-folder' : 'mdi-file-document'" class="mr-2"></v-icon>
                          {{ item.displayName }}
                        </td>
                        <td>{{ item.fileType }}</td>
                        <td>{{ formatStorage(item.fileSize) }}</td>
                        <td>
                          <v-tooltip :text="formatDate(item.createdDate, true)" location="bottom">
                            <template v-slot:activator="{ props }">
                              <span v-bind="props">{{ formatDate(item.createdDate) }}</span>
                            </template>
                          </v-tooltip>
                        </td>
                        <td>{{ getTeamName(item.createdByTeamId) }}</td>
                        <td>
                          <v-tooltip
                            :text="getUserName(item.createdByUserId)"
                            location="bottom"
                          >
                            <template v-slot:activator="{ props }">
                              <v-avatar
                                v-bind="props"
                                :size="28"
                                class="mr-1"
                              >
                                <v-img
                                  v-if="getUserAvatar(item.createdByUserId)"
                                  :src="'https://' + getUserAvatar(item.createdByUserId)"
                                  alt="User Avatar"
                                ></v-img>
                                <span
                                  v-else
                                  class="d-flex justify-center align-center text-caption white--text"
                                  :style="{
                                    backgroundColor: getRandomColor(item.createdByUserId),
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%'
                                  }"
                                >
                                  {{ getInitials(item.userDisplayName) }}
                                </span>
                              </v-avatar>
                            </template>
                          </v-tooltip>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script src="./model.js"></script>
<style src="./style.css" scoped></style>
